import {inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {ApiExecutor} from '@app/core/api';
import type {
  BulkPreviewResult,
  Contact,
  ContactsListQuery,
  ContactsListResult,
  DeleteContactPayload,
  ExcelColumnMapping,
  ExcelFirstStepResult,
  UpdateContactPayload,
} from '../types/contacts.types';
import {ALL_CONTACTS_GROUP_ID} from '../types/contacts.types';
import type {CreateContactRequestDto} from '../dto/create-contact-request.dto';
import {
  jsonStringBody,
  mapBulkPreviewModel,
  mapContactDetailModel,
  mapContactsPage,
  toIdNumber,
  unwrapApiResult,
} from '../utils/contacts.mappers';
import type {ContactsApi} from './contacts-api.port';
import {ContactSystemApiService, ContactsApiService} from './contacts-api.service';

@Injectable()
export class ContactsApiHttpAdapter implements ContactsApi {
  private readonly http = inject(ContactsApiService);
  private readonly systemApi = inject(ContactSystemApiService);
  private readonly apiExecutor = inject(ApiExecutor);

  listContacts(query: ContactsListQuery): Observable<ContactsListResult> {
    const requestQuery = {
      pageNumber: query.page ?? 1,
      pageSize: query.pageSize,
      phrase: query.phrase,
    };

    if (query.groupId === ALL_CONTACTS_GROUP_ID) {
      return this.systemApi.listContacts(requestQuery).pipe(
        map(response => mapContactsPage(unwrapApiResult(response))),
      );
    }

    const groupId = toIdNumber(query.groupId);
    return this.http
      .listContacts(groupId, requestQuery)
      .pipe(
        map(response =>
          mapContactsPage(unwrapApiResult(response), {groupName: query.groupName}),
        ),
      );
  }

  getContact(groupId: string, contactId: string): Observable<Contact | null> {
    return this.getContactWithEvents(groupId, contactId, new Map());
  }

  getContactWithEvents(
    groupId: string,
    contactId: string,
    eventNameById: Map<string, string>,
    groupName?: string,
  ): Observable<Contact | null> {
    return this.http.getContact(toIdNumber(groupId), toIdNumber(contactId)).pipe(
      map(response => {
        if (!response.success) return null;
        return mapContactDetailModel(response.result, {eventNameById, groupName});
      }),
    );
  }

  createContact(
    groupId: string,
    payload: UpdateContactPayload & {mobile: string},
  ): Observable<void> {
    return this.http
      .createContact(toIdNumber(groupId), this.toContactBody(payload))
      .pipe(map(response => unwrapApiResult(response)), map(() => undefined));
  }

  updateContact(
    groupId: string,
    contactId: string,
    payload: UpdateContactPayload,
  ): Observable<void> {
    return this.http
      .updateContact(toIdNumber(groupId), toIdNumber(contactId), this.toContactBody(payload))
      .pipe(
        map(response => unwrapApiResult(response)),
        map(() => undefined),
      );
  }

  deleteContact(payload: DeleteContactPayload): Observable<void> {
    const contactId = toIdNumber(payload.contactId);
    const request$ = payload.deleteGlobally
      ? this.systemApi.deleteFromSystem(contactId)
      : this.http.deleteFromGroup(toIdNumber(payload.groupId), contactId);

    return request$.pipe(map(response => unwrapApiResult(response)), map(() => undefined));
  }

  bulkPreview(groupId: string, mobiles: number[]): Observable<BulkPreviewResult> {
    return this.http.bulkPreview(toIdNumber(groupId), mobiles).pipe(
      map(response => mapBulkPreviewModel(unwrapApiResult(response))),
    );
  }

  bulkConfirm(groupId: string, key: string): Observable<void> {
    return this.http
      .bulkConfirm(toIdNumber(groupId), jsonStringBody(key))
      .pipe(map(response => unwrapApiResult(response)), map(() => undefined));
  }

  textUpload(groupId: string, file: File): Observable<BulkPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.textUpload(toIdNumber(groupId), formData).pipe(
      map(response => mapBulkPreviewModel(unwrapApiResult(response))),
    );
  }

  excelFirstStep(groupId: string, file: File): Observable<ExcelFirstStepResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.excelFirstStep(toIdNumber(groupId), formData).pipe(
      map(response => {
        const result = unwrapApiResult(response);
        return {key: result.key, columnTitles: result.columnTitles};
      }),
    );
  }

  excelSecondStep(
    groupId: string,
    key: string,
    columns: ExcelColumnMapping,
  ): Observable<BulkPreviewResult> {
    return this.http
      .excelSecondStep(toIdNumber(groupId), {key, columns})
      .pipe(map(response => mapBulkPreviewModel(unwrapApiResult(response))));
  }

  fileConfirm(groupId: string, key: string, shouldModifyExistContacts: boolean): Observable<void> {
    return this.http
      .fileConfirm(toIdNumber(groupId), {key, shouldModifyExistContacts})
      .pipe(map(response => unwrapApiResult(response)), map(() => undefined));
  }

  getExportUrl(groupId: string): string {
    const base = this.apiExecutor.getBaseUrl().replace(/\/$/, '');
    return `${base}/api/app/contact-group/${groupId}/contact/excel`;
  }

  private toContactBody(
    payload: UpdateContactPayload & {mobile?: string},
  ): CreateContactRequestDto {
    return {
      firstName: payload.firstName ?? '',
      lastName: payload.lastName ?? '',
      mobile: payload.mobile ? Number(payload.mobile) : 0,
      email: payload.email ?? '',
      address: payload.address ?? null,
      isMale: payload.isMale ?? null,
      userEvents: payload.userEvents ?? [],
    };
  }
}
